import { API_URLS } from "@/constants";
import apiRequest from "@/lib/axios";
import {
  BalanceAdjustment,
  GetList,
  PagingResponse,
  PaymentTransaction,
  PaymentTransactionFilter,
  UserWallet,
} from "@/types";

export const getPaymentTransactions = async (
  data: GetList<PaymentTransactionFilter>,
): Promise<PagingResponse<PaymentTransaction>> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.GET_PAYMENTS_LIST,
    data,
    errorMessage: "Failed to load the payment transactions. Please try again.",
  });
};

export const getUserWallets = async (userId: string): Promise<UserWallet[]> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.PAYMENT.USER_WALLETS(userId),
    errorMessage: "Failed to load the balances. Please try again.",
  });
};

interface AdminDepositPayload {
  userId: string;
  amount: number;
  currency: string;
  idempotencyKey?: string;
  note?: string;
}

export const adminDeposit = async (data: AdminDepositPayload): Promise<BalanceAdjustment> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.DEPOSIT,
    data,
    errorMessage: "Failed to process the deposit. Please try again.",
  });
};

export const adminWithdraw = async (data: AdminDepositPayload): Promise<BalanceAdjustment> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.WITHDRAW,
    data,
    errorMessage: "Failed to process the withdrawal. Please try again.",
  });
};

// Approve a player's pending withdraw request: the held funds leave the platform
// and the transaction is marked Completed.
export const approveWithdrawRequest = async (
  id: string,
  riskOverride?: string,
): Promise<unknown> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.WITHDRAW_APPROVE(id),
    data: { riskOverride },
    errorMessage: "Failed to approve the withdrawal. Please try again.",
  });
};

// Reject a player's pending withdraw request: the held funds are returned to the
// player's balance and the transaction is marked Cancelled.
export const rejectWithdrawRequest = async (id: string, reason?: string): Promise<unknown> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.WITHDRAW_REJECT(id),
    data: { reason },
    errorMessage: "Failed to reject the withdrawal. Please try again.",
  });
};

export interface MyBalance {
  balance: number;
  currency: string;
}

// Dedupe concurrent in-flight requests so React 18 StrictMode's double-mount
// (and any other simultaneous callers) share a single network call. Cleared
// when the request settles so subsequent reads re-fetch fresh data.
let inFlightBalance: Promise<MyBalance> | null = null;

export const getMyBalance = async (): Promise<MyBalance> => {
  if (inFlightBalance) return inFlightBalance;
  inFlightBalance = apiRequest<MyBalance>({
    method: "GET",
    url: API_URLS.PAYMENT.MY_BALANCE,
    errorMessage: "Failed to load the balance. Please try again.",
  }).finally(() => {
    inFlightBalance = null;
  });

  return inFlightBalance;
};

// ---- Crypto payouts --------------------------------------------------------
//
// Approving a crypto withdrawal does not settle it: it is dispatched to the TON
// gateway and only settles when the gateway confirms it on chain. These cover
// the states that follow.

// Re-dispatches a withdrawal whose gateway call did not get through. Safe to
// press repeatedly - the gateway keys on our withdrawal id and returns the
// payout it already has rather than creating a second one.
export const retryCryptoPayout = async (id: string): Promise<unknown> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.WITHDRAW_CRYPTO_RETRY(id),
    errorMessage: "Failed to re-dispatch the payout. Please try again.",
  });
};

// Re-reads the payout from the gateway and applies whatever it says. The
// recovery tool for a missed webhook; a payout still in flight is reported and
// left held, never failed.
export const recheckCryptoPayout = async (id: string): Promise<unknown> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.WITHDRAW_CRYPTO_RECHECK(id),
    errorMessage: "Failed to re-check the payout. Please try again.",
  });
};

// Only possible while the gateway has not broadcast the payout. After that it
// refuses, because on-chain is on-chain.
export const cancelCryptoPayout = async (id: string, reason?: string): Promise<unknown> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.WITHDRAW_CRYPTO_CANCEL(id),
    data: { reason },
    errorMessage: "Failed to cancel the payout. Please try again.",
  });
};

// Returns the hold of a withdrawal the gateway never took (no payout id). Refused once a payout
// exists: cancel it instead.
export const failUndispatchedCryptoPayout = async (id: string, note: string): Promise<unknown> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.WITHDRAW_CRYPTO_FAIL_UNDISPATCHED(id),
    data: { note },
    errorMessage: "Failed to fail the withdrawal. Please try again.",
  });
};

export interface CreditFromGatewayResult {
  credited: boolean;
  balance: number;
  transactionId: string;
  state: string;
}

// Credits a deposit from the gateway's record; a webhook that lands later is a no-op.
export const creditCryptoDepositFromGateway = async (
  depositId: string,
): Promise<CreditFromGatewayResult> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.PAYMENT.CRYPTO_DEPOSIT_CREDIT_FROM_GATEWAY(depositId),
    errorMessage: "Failed to credit the deposit from the gateway. Please try again.",
  });
};

// ---- Crypto currencies -----------------------------------------------------

export interface CryptoAssetSummary {
  code: string;
  name: string;
  kind: string;
  decimals: number;
  network: string;
  // Older gateways omit these and are TON only.
  chain?: string;
  chainName?: string;
  tokenStandard?: string | null;
  tokenContract?: string | null;
  image: string;
  depositEnabled: boolean;
  withdrawEnabled: boolean;
  // Raw integers in the asset's own smallest unit, not account-currency
  // amounts: 1 USDT is "1000000" and 1 TON is "1000000000", so `decimals`
  // above is what makes them readable.
  minDepositRaw: string;
  maxDepositRaw: string;
  minWithdrawRaw: string;
  maxWithdrawRaw: string;
  depositFeeBps: number;
  withdrawFeeBps: number;
  memoSupported: boolean;
  confirmations: number;
}

// What the gateway will accept right now, per currency.
//
// Returns `enabled: false` with an empty list when the gateway is off or
// unreachable rather than throwing, so the screen it is shown on still loads.
export const getCryptoAssets = async (): Promise<{
  enabled: boolean;
  data: CryptoAssetSummary[];
}> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.PAYMENT.CRYPTO_ASSETS,
    errorMessage: "Failed to load the crypto currencies. Please try again.",
  });
};
